import React, { useMemo } from 'react';
import classNames from 'classnames';

import { action } from 'mobx';
import { observer } from 'mobx-react';

import { useStyles } from 'ui/base/theme';
import { BusyStore } from 'ui/base/button';
import { ChoiceStore } from 'ui/base/input';
import { StandardPage } from 'ui/base/page/standard_page';

import { RadioInput } from 'ui/standard/input/radio_input';

import { ActiveButton, DefaultButton } from 'ui/public/button';

import { ErrorMessage } from 'ui/public/error_message/error_message';

import { Optional } from 'services/base/types';
import { GlobalLocaleStore, localeToLanguageCode } from 'services/base/i18n';

import {
  PlanFirewallBilling,
  PlanFirewallPackage,
} from 'services/accounts/types';

import { PlanFirewallService } from 'services/accounts/plans_service';

import {
  getPlanFirewallBillingTier,
  getPlanFirewallPackageInfo,
  getPlanFirewallPackageName,
  getPlanFirewallPackageTier,
  getPlanFirewallPriceActive,
  getPlanFirewallPricePerText,
  getPlanFirewallSubscriptionActive,
  getPlanFirewallTierBilling,
} from 'services/accounts/logic/plans/plan_firewall';

import { Currency } from 'assets/enums/currencies/currencies';

import {
  themedPlanFirewallPackageIconStyles,
  getPlanFirewallPackageIcon,
} from 'assets/enums/plans/firewall/plan_firewall';

import { PlanItemFirewallBaseOptionalModalProps } from '../plan_item_firewall';

import ManagePlansMessages from '../../../../manage_plans_messages';
import PlanFirewallMessages from '../plan_item_firewall_messages';
import Messages from './plan_firewall_package_modal_messages';

type PlanItemFirewallPackageInputRowProps = PlanItemFirewallBaseOptionalModalProps & {
  choices: ChoiceStore<PlanFirewallPackage>;
  choice: PlanFirewallPackage;
};
const PlanItemFirewallPackageInputRow = observer(({ styles, accounts, plan, store, choices, choice } : PlanItemFirewallPackageInputRowProps) => {
  const icons = useStyles(themedPlanFirewallPackageIconStyles);

  // Our priority is to show all available packages regardless of the selected billing option.
  // The selected billing option should only serve as a hint as to what price to display for a
  // package. In case the selected billing option is unavailable for this package, we should do
  // our best to find the closest available billing option so that we can show a price for this
  // package.

  const billing_id = useMemo(() => {
    let billing_id = store.billing_store.value;
    let billing_tier = getPlanFirewallBillingTier(billing_id);
    let billing_price = billing_id && getPlanFirewallPricePerText(accounts, choice, billing_id, Currency.USD, false);
    let billing_active = billing_id && getPlanFirewallPriceActive(accounts, choice, billing_id);
    while (!billing_active || !billing_price) {
      billing_id = getPlanFirewallTierBilling(billing_tier + 1);
      billing_tier = getPlanFirewallBillingTier(billing_id);
      billing_price = billing_id && getPlanFirewallPricePerText(accounts, choice, billing_id, Currency.USD, false);
      billing_active = billing_id && getPlanFirewallPriceActive(accounts, choice, billing_id);
      if (billing_id === store.billing_store.value) break;
    }
    return billing_id ?? PlanFirewallBilling.YEARLY;
  }, [store.billing_store.value, accounts, choice]);

  const package_name = getPlanFirewallPackageName(choice);
  const package_icon = getPlanFirewallPackageIcon(icons, choice);
  const package_info = getPlanFirewallPackageInfo(choice);
  const package_price = getPlanFirewallPricePerText(accounts, choice, billing_id, Currency.USD, false);
  const package_active = getPlanFirewallPriceActive(accounts, choice, billing_id);

  const tag_lifetime = choice === plan?.license?.package;

  const tag_list = [
    ...(tag_lifetime ? [PlanFirewallMessages.tagLifetime()] : []),
  ];

  const selected = choice === choices.selected;

  const disabled = (
    !package_price || !package_active || (plan?.license &&
      getPlanFirewallPackageTier(choice) <
      getPlanFirewallPackageTier(plan.license.package)
    )
  );

  const choice_store = React.useCallback(action(() => {
    if (!disabled) choices.setSelected(choice);
  }), [choices]);

  return (<>
    {!disabled && (
      <div onClick={choice_store}
          className={classNames(!disabled && styles.clickable,
                                selected ? styles.cardSelected :
                                disabled ? styles.cardDisabled :
                                styles.cardDefault, styles.field)}>
        <img className={styles.cardIcon} src={package_icon} alt='Package Icon' />
        <div className={styles.cardText}>
          <div className={styles.cardName}>
            {package_name}
            <span className={styles.cardInfo}>
              ({package_price ? !tag_lifetime ? package_price
                              : PlanFirewallMessages.priceFree()
                              : PlanFirewallMessages.priceUnavailable()})
            </span>
          </div>
          <div className={styles.cardInfo}>
            {package_info}
          </div>
          {tag_list.length > 0 && (
            <div className={styles.cardTags}>
              {tag_list.map((tag, index) => (
                <span key={`tag-${index}`}>
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
        <RadioInput
          store={choices}
          choice={choice}
        />
      </div>
    )}
  </>);
});

type PlanItemFirewallPackageModalProps = PlanItemFirewallBaseOptionalModalProps;
export const PlanItemFirewallPackageModal = observer((props: PlanItemFirewallPackageModalProps) => {
  const { styles, accounts, pangolin, plan, store, modal, root } = props;

  const disabled = store.package_store.value === undefined;

  const licensePackage = plan?.license?.package;
  const subscriptionPackage = plan?.subscription?.package;
  const targetPackage = store.package_store.value;

  const [busy] = React.useState(new BusyStore());
  const [error, setError] = React.useState<Optional<string>>(undefined);
  const [warning, setWarning] = React.useState<Optional<string>>(undefined);

  const locale = localeToLanguageCode(GlobalLocaleStore.get());
  const format: object = {
    month: 'short',
    year: 'numeric',
    day: 'numeric',
    weekday: 'short',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  };

  // Handle warning
  React.useEffect(() => {
    if (plan && getPlanFirewallSubscriptionActive(plan)) {
      const targetPackageTier = getPlanFirewallPackageTier(targetPackage);
      const subscriptionPackageTier = getPlanFirewallPackageTier(subscriptionPackage);
      if (targetPackageTier <= subscriptionPackageTier) {
        // All scenarios where the current subscription is retained are
        // conditional on whether the user is purchasing a package tier
        // lower than the current subscription package tier.
        //
        // You may refer to the scenarios outlined in the previewPlanChange
        // method in: services/accounts/logic/plans/plan_firewall.ts
        if (plan.subscription) {
          setWarning(PlanFirewallMessages.warningEffectivity(
            plan.subscription.valid_until.toLocaleString(locale, format)
          ));
        }
      } else {
        setWarning(undefined);
      }
    }
  }, [plan, licensePackage, subscriptionPackage, targetPackage, locale, format]);

  // Handle navigation
  const onNext = React.useCallback(async() => {
    if (!disabled) {
      // Check if the user has not changed the plan details
      const changePlanDetails = (
        store.package_store.value !== store.package_default
      );

      if (changePlanDetails) {
        // If plan details have changed...

        // Set subsequent steps to undefined
        store.billing_store.setSelected(undefined);
        store.payment_store.setSelected(undefined);
        store.preview_is_trialing_store.setValue(false);
        store.preview_trial_until_store.setValue(undefined);
        store.preview_charges_store.setValue(undefined);
        store.preview_refunds_store.setValue(undefined);
        store.preview_notes_store.setValue(undefined);
      } else {
        // If the plan details are the same...

        // Set subsequent steps to default
        store.billing_store.setSelected(store.billing_default);
        store.payment_store.setSelected(store.payment_default);
      }

      // Check if the user is switching to an owned license
      const changeToOwnedLicense = (
        getPlanFirewallPackageTier(store.package_store.value) <=
        getPlanFirewallPackageTier(plan?.license?.package)
      );

      if (changeToOwnedLicense) {
        // If the user is switching to an owned license...

        // Configure store for owned license
        store.billing_store.setSelected(PlanFirewallBilling.LIFETIME);
        store.payment_store.setSelected(undefined);

        // Clear error string
        setError(undefined);

        // Clear preview data
        store.preview_is_trialing_store.setValue(false);
        store.preview_trial_until_store.setValue(undefined);
        store.preview_charges_store.setValue(undefined);
        store.preview_refunds_store.setValue(undefined);
        store.preview_notes_store.setValue(undefined);

        // Retrieve invoice preview
        const preview = await busy.perform(async() => {
          try {
            modal.freezeModal();
            return await PlanFirewallService.preview(
              accounts, pangolin,
              store.payment_store.value!,
              store.package_store.value!,
              store.billing_store.value!,
            );
          } finally {
            modal.unfreezeModal();
          }
        });

        if (preview.success) {
          // Go to confirm modal on success
          store.preview_is_trialing_store.setValue(preview.is_trialing);
          store.preview_trial_until_store.setValue(preview.trial_until);
          store.preview_charges_store.setValue(preview.charges);
          store.preview_refunds_store.setValue(preview.refunds);
          store.preview_notes_store.setValue(preview.notes);
          modal.history.push(`${root}/confirm`);
        } else {
          // Set error string on failure
          setError(PlanFirewallMessages.errorInvoice());
        }
      } else {
        // If the user is switching to a new arrangement...

        // Go to billing modal
        modal.history.push(`${root}/billing`);
      }
    }
  }, [modal, accounts, pangolin, root, busy, store, plan, disabled]);

  return (
    <div className={classNames(styles.modalContainer, styles.modalContainerPopOut)}>
      <StandardPage>
        <div className={styles.modalHeaderRow}>
          {Messages.name()}
          <button
            className={styles.modalCloseIcon}
            onClick={modal.dismissModal}
            type='button'
          />
        </div>
        <div className={styles.modalContentRow}>
          <div className={styles.section}>
            <PlanItemFirewallPackageInputRow
              choice={PlanFirewallPackage.BASIC}
              choices={store.package_store}
              {...props}
            />
            <PlanItemFirewallPackageInputRow
              choice={PlanFirewallPackage.BUSINESS}
              choices={store.package_store}
              {...props}
            />
            <PlanItemFirewallPackageInputRow
              choice={PlanFirewallPackage.ENTERPRISE}
              choices={store.package_store}
              {...props}
            />
            {error && (
              <div className={styles.notes}>
                <ErrorMessage message={error} />
              </div>
            )}
            {warning && (
              <div className={styles.notes}>
                <ErrorMessage message={warning} />
              </div>
            )}
          </div>
          <ActiveButton onClick={onNext} disabled={disabled}>
            {ManagePlansMessages.actionNext()}
          </ActiveButton>
          <DefaultButton type='button' onClick={modal.history.goBack}>
            {ManagePlansMessages.actionBack()}
          </DefaultButton>
        </div>
      </StandardPage>
    </div>
  );
});
